import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * Administration → Client Access: who may ask the assistant and the MCP
 * endpoint about which client.
 *
 * The matrix behind the client-assignment gate (API
 * `staff-client-assignments`; rulings IC-HND-20260909 §1–§3). The gate reads
 * CONFIRMED rows only. Rows HQ proposes from meeting attendance ("derive") land
 * unconfirmed and grant nothing until an administrator confirms them here. A
 * grant made here is confirmed on arrival: the administrator's act is the
 * confirmation.
 *
 * Nothing on this screen switches the gate on. That is the
 * `HQ_CLIENT_ASSIGNMENT_GATE` setting on the API container, and it ships off.
 */

export interface AssignmentEvidence {
  basis?: string;
  meetings?: number;
  lastMeetingAt?: string | null;
  meetingIds?: number[];
  since?: string | null;
}

/** One row as the API returns it (staff-client-assignments.schema.ts). */
export interface StaffClientAssignment {
  id: number;
  userId: number;
  clientId: string;
  source: "manual" | "attendance";
  evidence: AssignmentEvidence | null;
  confirmed: boolean;
  confirmedAt: string | null;
  confirmedBy: number | null;
  createdAt: string | null;
  createdBy: number | null;
  updatedAt: string | null;
  userEmail: string | null;
  clientName: string | null;
  clientCode: string | null;
}

export type AccessFilter = "all" | "proposed" | "confirmed";

export interface UserSummary {
  id: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  isValidated?: boolean;
}

interface ClientSummary {
  id: string;
  name?: string;
  code?: string;
}

/** The API's cap on one find (staff-client-assignments.schema.ts `$limit` maximum). */
export const ASSIGNMENTS_SERVER_LIMIT = 1000;

/** Proposed rows first (they are the work), then by client, then by person. */
export const sortAssignments = (
  rows: StaffClientAssignment[],
): StaffClientAssignment[] =>
  [...rows].sort(
    (a, b) =>
      Number(a.confirmed) - Number(b.confirmed) ||
      (a.clientName ?? a.clientCode ?? "").localeCompare(
        b.clientName ?? b.clientCode ?? "",
      ) ||
      (a.userEmail ?? "").localeCompare(b.userEmail ?? ""),
  );

/** "3 meetings, last 24 Sep" — why HQ proposed a row. Empty for a manual grant. */
export const describeEvidence = (row: StaffClientAssignment): string => {
  if (row.source !== "attendance" || !row.evidence) return "";
  const n = row.evidence.meetings ?? 0;
  const last = row.evidence.lastMeetingAt
    ? `, last ${new Date(row.evidence.lastMeetingAt).toLocaleDateString()}`
    : "";
  return `Attended ${n} meeting${n === 1 ? "" : "s"}${last}`;
};

class ClientAccessStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  filter: AccessFilter = "all";
  searchQuery = "";
  /** Row ids with a confirm / un-confirm in flight. */
  busyIds: number[] = [];
  deriving = false;

  grantForm: { userId: number | null; clientId: string } = {
    userId: null,
    clientId: "",
  };
  grantError: string | null = null;
  granting = false;

  removeTarget: StaffClientAssignment | null = null;
  removing = false;
  /** Why the last remove failed — the route throws it into the dialog's footer. */
  removeError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get service(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore(
        "staff-client-assignments",
      ) ??
        (this.rootStore as unknown as Record<string, unknown>)
          .staffClientAssignmentsStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  private get usersService(): ServiceStoreType | null {
    return (
      (this.rootStore.userStore as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get clientsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("clients") ??
        this.rootStore.clientsStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  // ---- Derived data (entities live in the ServiceStore caches) -------------

  get rows(): StaffClientAssignment[] {
    return (this.service?.entities ?? []) as unknown as StaffClientAssignment[];
  }

  get counts(): { proposed: number; confirmed: number } {
    const confirmed = this.rows.filter((r) => r.confirmed).length;
    return { proposed: this.rows.length - confirmed, confirmed };
  }

  get visibleRows(): StaffClientAssignment[] {
    const q = this.searchQuery.trim().toLowerCase();
    return sortAssignments(
      this.rows.filter((r) => {
        if (this.filter === "proposed" && r.confirmed) return false;
        if (this.filter === "confirmed" && !r.confirmed) return false;
        if (!q) return true;
        return [r.userEmail, r.clientName, r.clientCode]
          .join(" ")
          .toLowerCase()
          .includes(q);
      }),
    );
  }

  get truncated(): boolean {
    return this.rows.length >= ASSIGNMENTS_SERVER_LIMIT;
  }

  /** Live staff for the grant picker, by email. */
  get users(): UserSummary[] {
    // The user service returns ids as numbers or strings; a grant needs an integer.
    return (
      (this.usersService?.entities ?? []) as unknown as Array<
        UserSummary & { id: number | string }
      >
    )
      .filter(
        (u) =>
          u.isValidated !== false &&
          !!u.email &&
          Number.isInteger(Number(u.id)),
      )
      .map((u) => ({ ...u, id: Number(u.id) }))
      .sort((a, b) => (a.email ?? "").localeCompare(b.email ?? ""));
  }

  get clients(): ClientSummary[] {
    return [
      ...((this.clientsService?.entities ?? []) as unknown as ClientSummary[]),
    ].sort((a, b) =>
      (a.name ?? a.code ?? "").localeCompare(b.name ?? b.code ?? ""),
    );
  }

  isBusy(id: number): boolean {
    return this.busyIds.includes(id);
  }

  setFilter(value: AccessFilter) {
    this.filter = value;
  }

  setSearchQuery(value: string) {
    this.searchQuery = value;
  }

  // ---- Loading --------------------------------------------------------------

  async load() {
    await Promise.all([this.loadAssignments(), this.loadDirectory()]);
  }

  async loadAssignments() {
    this.loading = true;
    this.error = null;
    try {
      // The whole matrix, filtered here: the ServiceStore replaces its cache on
      // every find (serviceStores/administration.stores.ts), so a server-side
      // filter would make rows vanish from the other tabs.
      //
      // `findOrThrow`: the ServiceStore swallows a failed find (resolves null),
      // which rendered "No client access recorded yet." over a failed load.
      await findOrThrow(this.service, {
        query: { $limit: ASSIGNMENTS_SERVER_LIMIT },
      });
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load client access");
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Best-effort: the grant picker's people and clients. */
  async loadDirectory() {
    try {
      await Promise.all([
        this.usersService?.find({ query: { $limit: 500 } }),
        this.clientsService?.find({
          query: { $limit: 500, $sort: { name: 1 } },
        }),
      ]);
    } catch {
      // The table still reads without them: rows carry their own display names.
    }
  }

  // ---- Actions ----------------------------------------------------------------

  /** Propose rows from meeting attendance. Idempotent: an existing pair is left alone. */
  async derive(): Promise<number | null> {
    if (this.deriving) return null;
    this.deriving = true;
    try {
      const created = (await this.service?.create({
        action: "derive",
      } as never)) as unknown;
      const n = Array.isArray(created) ? created.length : 0;
      this.rootStore.notificationStore?.addSuccessNotification(
        n === 0
          ? "No new proposals: every pair seen in meetings is already listed."
          : `Proposed ${n} from meeting attendance. They grant nothing until confirmed.`,
      );
      await this.loadAssignments();
      return n;
    } catch (err) {
      if (!isSessionEnded(err)) {
        this.rootStore.notificationStore?.addErrorNotification(
          err instanceof Error ? err.message : "Could not propose from meetings",
        );
      }
      return null;
    } finally {
      runInAction(() => {
        this.deriving = false;
      });
    }
  }

  async setConfirmed(
    row: StaffClientAssignment,
    confirmed: boolean,
  ): Promise<boolean> {
    if (this.isBusy(row.id)) return false;
    this.busyIds = [...this.busyIds, row.id];
    try {
      await this.service?.patch(row.id, { confirmed } as never);
      await this.loadAssignments();
      return true;
    } catch (err) {
      if (!isSessionEnded(err)) {
        this.rootStore.notificationStore?.addErrorNotification(
          err instanceof Error ? err.message : "Could not update the assignment",
        );
      }
      return false;
    } finally {
      runInAction(() => {
        this.busyIds = this.busyIds.filter((id) => id !== row.id);
      });
    }
  }

  // ---- Grant dialog (route-registered; see clientAccess.Route.ts) ------------

  openGrantDialog() {
    this.grantForm = { userId: null, clientId: "" };
    this.grantError = null;
    this.rootStore.routerStore?.openDialog?.({
      dialogName: "GrantClientAccessDialog",
    });
  }

  setGrantUser(userId: number | null) {
    this.grantForm = { ...this.grantForm, userId };
  }

  setGrantClient(clientId: string) {
    this.grantForm = { ...this.grantForm, clientId };
  }

  /** The pair is already on the list (confirmed or proposed). */
  get grantDuplicate(): StaffClientAssignment | null {
    const { userId, clientId } = this.grantForm;
    if (userId == null || !clientId) return null;
    return (
      this.rows.find(
        (r) =>
          r.userId === userId &&
          r.clientId.toLowerCase() === clientId.toLowerCase(),
      ) ?? null
    );
  }

  get canSubmitGrant(): boolean {
    return (
      this.grantForm.userId != null &&
      !!this.grantForm.clientId &&
      !this.grantDuplicate &&
      !this.granting
    );
  }

  /** @returns true when the grant landed (the route keeps the dialog open with `grantError` otherwise). */
  async submitGrant(): Promise<boolean> {
    if (!this.canSubmitGrant) return false;
    this.granting = true;
    this.grantError = null;
    try {
      await this.service?.create({
        userId: this.grantForm.userId,
        clientId: this.grantForm.clientId,
      } as never);
      this.rootStore.notificationStore?.addSuccessNotification(
        "Access granted",
      );
      await this.loadAssignments();
      return true;
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.grantError =
            err instanceof Error ? err.message : "Could not grant access";
        }
      });
      return false;
    } finally {
      runInAction(() => {
        this.granting = false;
      });
    }
  }

  // ---- Remove dialog -----------------------------------------------------------

  requestRemove(row: StaffClientAssignment) {
    this.removeTarget = row;
    this.removeError = null;
    this.rootStore.routerStore?.openDialog?.({
      dialogName: "RemoveClientAccessDialog",
    });
  }

  cancelRemove() {
    if (this.removing) return;
    this.removeTarget = null;
  }

  async executeRemove(): Promise<boolean> {
    const target = this.removeTarget;
    if (!target || this.removing) return false;
    this.removing = true;
    this.removeError = null;
    try {
      await this.service?.remove(target.id);
      runInAction(() => {
        this.removeTarget = null;
      });
      await this.loadAssignments();
      return true;
    } catch (err) {
      // No toast: the route throws this into the still-open dialog's footer.
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.removeError =
            err instanceof Error ? err.message : "Could not remove the assignment";
        }
      });
      return false;
    } finally {
      runInAction(() => {
        this.removing = false;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.filter = "all";
    this.searchQuery = "";
    this.busyIds = [];
    this.deriving = false;
    this.grantForm = { userId: null, clientId: "" };
    this.grantError = null;
    this.granting = false;
    this.removeTarget = null;
    this.removing = false;
    this.removeError = null;
  }
}

export default ClientAccessStore;
